const IP_ADDRESS_REGEX = /^\d+\.\d+\.\d+\.\d+$/;
const IPV6_ADDRESS_REGEX = /^[0-9a-f:]+$/i;

function normalizeHostname(hostname: string): string {
  return hostname.trim().replace(/\.$/, "").toLowerCase();
}

function getConfiguredSubdomainBaseDomains(): string[] {
  const configured =
    (import.meta.env.VITE_ORG_SUBDOMAIN_BASE_DOMAINS as string | undefined) ||
    (import.meta.env.VITE_ORG_BASE_DOMAIN as string | undefined) ||
    "";

  return configured
    .split(",")
    .map((value) => normalizeHostname(value))
    .filter(
      (value, index, values) => value.length > 0 && values.indexOf(value) === index,
    );
}

function extractSlugForBaseDomain(
  hostname: string,
  baseDomain: string,
): string | null {
  if (hostname === baseDomain || !hostname.endsWith(`.${baseDomain}`)) {
    return null;
  }

  const prefix = hostname.slice(0, -(baseDomain.length + 1));
  if (!prefix || prefix.includes(".")) {
    return null;
  }

  return /^[a-z0-9-]{2,100}$/.test(prefix) ? prefix : null;
}

export const isLocalHost = (hostname: string): boolean => {
  const normalized = normalizeHostname(hostname);
  return (
    normalized === "localhost" ||
    normalized === "127.0.0.1" ||
    normalized === "::1"
  );
};

export const getOrganizationSlugFromHostname = (
  hostname: string,
): string | null => {
  const normalizedHostname = normalizeHostname(hostname);

  if (
    isLocalHost(normalizedHostname) ||
    IP_ADDRESS_REGEX.test(normalizedHostname) ||
    IPV6_ADDRESS_REGEX.test(normalizedHostname)
  ) {
    return null;
  }

  const parts = normalizedHostname.split(".");
  if (parts.length === 2 && parts[1] === "localhost") {
    const localSlug = parts[0]?.toLowerCase() || "";
    return /^[a-z0-9-]{2,100}$/.test(localSlug) ? localSlug : null;
  }

  for (const baseDomain of getConfiguredSubdomainBaseDomains()) {
    const slug = extractSlugForBaseDomain(normalizedHostname, baseDomain);
    if (slug) {
      return slug;
    }
  }

  return null;
};

export const getOrganizationLookupInputFromHostname = (
  hostname: string,
): { slug?: string; hostname?: string } | null => {
  const normalizedHostname = normalizeHostname(hostname);

  if (
    !normalizedHostname ||
    isLocalHost(normalizedHostname) ||
    IP_ADDRESS_REGEX.test(normalizedHostname) ||
    IPV6_ADDRESS_REGEX.test(normalizedHostname)
  ) {
    return null;
  }

  const slug = getOrganizationSlugFromHostname(normalizedHostname);
  return slug ? { slug } : { hostname: normalizedHostname };
};

export const shouldUseWorkspaceLookup = (
  hostname: string,
  forceDirectLogin: boolean,
): boolean => {
  if (forceDirectLogin) return false;
  return getOrganizationSlugFromHostname(hostname) === null;
};

export const buildOrgLoginUrl = (
  organizationSlug: string,
  currentOrigin: string,
): string => {
  const origin = new URL(currentOrigin);
  const host = origin.hostname;
  if (isLocalHost(host) || IP_ADDRESS_REGEX.test(host)) {
    const protocol = origin.protocol;
    const port = origin.port ? `:${origin.port}` : "";
    return `${protocol}//${organizationSlug}.localhost${port}/login`;
  }

  const explicitBaseDomain = getConfiguredSubdomainBaseDomains()[0];
  if (explicitBaseDomain) {
    return `https://${organizationSlug}.${explicitBaseDomain}/login`;
  }

  const parts = host.split(".");
  if (parts.length >= 2) {
    const baseDomain = `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
    return `https://${organizationSlug}.${baseDomain}/login`;
  }

  return `https://${organizationSlug}.nervedx.com/login`;
};
